import { Check, ChevronLeft, ChevronRight, Package, Truck } from "lucide-react";
import { useMemo, useState } from "react";

import { ErrorBanner } from "../components/ErrorBanner";
import { SectionLabel } from "../components/SectionLabel";
import { Skeleton } from "../components/Skeleton";
import { formatTrayFraction, type DeliveryGroup, type DeliveryItem } from "../lib/api";
import { addDays, formatDisplay, formatMonthDay, todayISO, type IsoDate } from "../lib/date";
import { useDeliveries, usePackHarvest } from "../lib/queries";
import { fonts, radius, shadow, tokens, trayColors } from "../styles/theme";

function weekRange(anchor: IsoDate): { from: IsoDate; to: IsoDate } {
  // Matches the API's default: Mon → Sun for the week containing `anchor`.
  const d = new Date(`${anchor}T12:00:00Z`);
  const day = d.getUTCDay();
  const daysFromMonday = (day + 6) % 7;
  const from = addDays(anchor, -daysFromMonday);
  const to = addDays(from, 6);
  return { from, to };
}

function isPending(item: DeliveryItem): boolean {
  return item.harvest_id.startsWith("pending:");
}

export function DeliveriesView() {
  const [anchor, setAnchor] = useState<IsoDate>(todayISO());
  const { from, to } = weekRange(anchor);
  const deliveries = useDeliveries(from, to);
  const pack = usePackHarvest();
  const [mutationError, setMutationError] = useState<string | null>(null);

  const groups = deliveries.data ?? [];
  const { upcoming, past } = useMemo(() => {
    const today = todayISO();
    const up: DeliveryGroup[] = [];
    const old: DeliveryGroup[] = [];
    for (const g of groups) {
      if (g.delivery_date >= today) up.push(g);
      else old.push(g);
    }
    return { upcoming: up, past: old };
  }, [groups]);

  return (
    <div>
      {mutationError && (
        <ErrorBanner message={mutationError} onDismiss={() => setMutationError(null)} />
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
          padding: "10px",
          background: tokens.card,
          border: `1px solid ${tokens.line}`,
          borderRadius: radius.md,
          boxShadow: shadow.xs,
        }}
      >
        <button
          type="button"
          onClick={() => setAnchor((a) => addDays(a, -7))}
          style={iconBtn}
          aria-label="Previous week"
        >
          <ChevronLeft size={18} />
        </button>
        <div style={{ textAlign: "center", flex: 1 }}>
          <div
            style={{
              fontSize: "20px",
              color: tokens.ink,
              lineHeight: 1.1,
            }}
          >
            {formatDisplay(from)} — {formatDisplay(to)}
          </div>
          <div
            style={{
              fontSize: "11px",
              color: tokens.muted,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              fontWeight: 600,
              marginTop: "4px",
            }}
          >
            Week pack list
          </div>
        </div>
        <button
          type="button"
          onClick={() => setAnchor((a) => addDays(a, 7))}
          style={iconBtn}
          aria-label="Next week"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {deliveries.isLoading && <Skeleton count={2} height={160} />}
      {deliveries.isError && (
        <ErrorBanner
          message={`Failed to load deliveries: ${(deliveries.error as Error).message}`}
        />
      )}

      {!deliveries.isLoading && groups.length === 0 && <DeliveriesEmpty />}

      {upcoming.length > 0 && (
        <div style={{ marginBottom: past.length > 0 ? "28px" : 0 }}>
          <SectionLabel>Upcoming</SectionLabel>
          {upcoming.map((g) => (
            <DeliveryPanel
              key={g.delivery_date}
              group={g}
              onToggle={(item, packed) => {
                if (isPending(item)) {
                  setMutationError(
                    "No harvest logged yet for this delivery. Add yield to enable packing.",
                  );
                  return;
                }
                pack.mutate(
                  { id: item.harvest_id, packed },
                  { onError: (err) => setMutationError(err.message) },
                );
              }}
            />
          ))}
        </div>
      )}

      {past.length > 0 && (
        <details
          style={{
            border: `1px solid ${tokens.line}`,
            borderRadius: radius.md,
            background: tokens.card,
            overflow: "hidden",
            boxShadow: shadow.xs,
          }}
        >
          <summary
            style={{
              padding: "14px 16px",
              minHeight: "44px",
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              fontSize: "11px",
              letterSpacing: "0.14em",
              color: tokens.muted,
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            Earlier this week · {past.length}
          </summary>
          <div style={{ padding: "0 10px 10px" }}>
            {past.map((g) => (
              <DeliveryPanel
                key={g.delivery_date}
                group={g}
                onToggle={(item, packed) => {
                  if (isPending(item)) {
                    setMutationError(
                      "No harvest logged yet for this delivery. Add yield to enable packing.",
                    );
                    return;
                  }
                  pack.mutate(
                    { id: item.harvest_id, packed },
                    { onError: (err) => setMutationError(err.message) },
                  );
                }}
              />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function DeliveryPanel({
  group,
  onToggle,
}: {
  group: DeliveryGroup;
  onToggle: (item: DeliveryItem, packed: boolean) => void;
}) {
  const packed = group.items.filter((i) => i.packed_at !== null).length;
  const total = group.items.length;
  const allPacked = total > 0 && packed === total;
  const weekday = new Date(`${group.delivery_date}T12:00:00Z`).toLocaleDateString("en-US", {
    weekday: "long",
    timeZone: "UTC",
  });

  return (
    <section
      aria-label={`Delivery ${group.delivery_date}`}
      style={{
        border: `1px solid ${tokens.line}`,
        borderLeft: `4px solid ${allPacked ? tokens.sage : tokens.amber}`,
        borderRadius: radius.md,
        marginBottom: "14px",
        background: tokens.card,
        overflow: "hidden",
        boxShadow: shadow.xs,
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: `1px solid ${tokens.line}`,
          gap: "12px",
        }}
      >
        <div style={{ display: "flex", gap: "10px", alignItems: "center", minWidth: 0 }}>
          <Truck
            size={16}
            color={allPacked ? tokens.sage : tokens.amber}
            style={{ flexShrink: 0 }}
          />
          <div>
            <div
              style={{
                fontSize: "16px",
                color: tokens.ink,
                lineHeight: 1.2,
              }}
            >
              {weekday}
            </div>
            <div style={{ fontSize: "12px", color: tokens.muted, marginTop: "1px" }}>
              {formatMonthDay(group.delivery_date)}
            </div>
          </div>
        </div>
        <span
          aria-label={`${packed} of ${total} packed`}
          style={{
            fontSize: "11px",
            fontWeight: 600,
            letterSpacing: "0.03em",
            padding: "4px 10px",
            borderRadius: radius.pill,
            background: allPacked ? tokens.sageSoft : tokens.sunken,
            color: allPacked ? tokens.sageDeep : tokens.inkSoft,
            whiteSpace: "nowrap",
          }}
        >
          {packed}/{total} packed
        </span>
      </header>
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {group.items.map((item, i) => (
          <DeliveryItemRow
            key={item.harvest_id}
            item={item}
            isLast={i === group.items.length - 1}
            onToggle={onToggle}
          />
        ))}
      </ul>
    </section>
  );
}

function DeliveryItemRow({
  item,
  isLast,
  onToggle,
}: {
  item: DeliveryItem;
  isLast: boolean;
  onToggle: (item: DeliveryItem, packed: boolean) => void;
}) {
  const packed = item.packed_at !== null;
  const trayColor = trayColors[item.tray_id] ?? tokens.faint;
  const pending = isPending(item);
  return (
    <li
      style={{
        display: "flex",
        gap: "12px",
        padding: "10px 14px",
        borderBottom: isLast ? "none" : `1px solid ${tokens.line}`,
        alignItems: "center",
        background: packed ? tokens.sunken : "transparent",
        opacity: packed ? 0.75 : 1,
      }}
    >
      <button
        type="button"
        aria-label={packed ? "Unpack item" : "Mark packed"}
        aria-pressed={packed}
        onClick={() => onToggle(item, !packed)}
        disabled={pending}
        style={{
          background: "transparent",
          border: "none",
          minWidth: "44px",
          minHeight: "44px",
          padding: 0,
          cursor: pending ? "not-allowed" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: pending ? 0.4 : 1,
        }}
      >
        <span
          aria-hidden
          style={{
            width: "24px",
            height: "24px",
            borderRadius: radius.sm,
            border: `2px solid ${packed ? tokens.sage : tokens.lineStrong}`,
            background: packed ? tokens.sage : tokens.card,
            color: "#fff",
            display: "grid",
            placeItems: "center",
            transition: "background 0.15s ease, border-color 0.15s ease, color 0.15s ease",
          }}
        >
          {packed && <Check size={14} strokeWidth={3} />}
        </span>
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            gap: "8px",
            alignItems: "center",
            flexWrap: "wrap",
            marginBottom: "2px",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "11px",
              color: tokens.muted,
              letterSpacing: "0.06em",
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          >
            <span
              aria-hidden
              style={{
                width: "8px",
                height: "8px",
                borderRadius: radius.pill,
                background: trayColor,
              }}
            />
            Tray {item.tray_id}
          </span>
          <span
            style={{
              fontSize: "14px",
              color: packed ? tokens.muted : tokens.ink,
              textDecoration: packed ? "line-through" : "none",
            }}
          >
            {item.crop}
            {item.tray_fraction < 0.999 && (
              <span style={{ color: tokens.muted }}>
                {" "}
                — {formatTrayFraction(item.tray_fraction)} tray
              </span>
            )}
          </span>
          {pending && (
            <span
              style={{
                fontSize: "10px",
                fontWeight: 600,
                letterSpacing: "0.03em",
                padding: "2px 8px",
                background: tokens.amberSoft,
                color: tokens.amberInk,
                borderRadius: radius.pill,
              }}
            >
              Pending
            </span>
          )}
        </div>
        <div
          style={{
            fontFamily: fonts.mono,
            fontSize: "11px",
            color: tokens.muted,
          }}
        >
          {item.seed_oz.toFixed(2)} oz seed →{" "}
          {item.yield_oz !== null ? `${item.yield_oz.toFixed(2)} oz` : "— oz"}
        </div>
      </div>
    </li>
  );
}

function DeliveriesEmpty() {
  return (
    <div
      style={{
        textAlign: "center",
        padding: "56px 20px",
        background: tokens.card,
        border: `1px solid ${tokens.line}`,
        borderRadius: radius.lg,
        boxShadow: shadow.xs,
      }}
    >
      <div
        style={{
          width: "56px",
          height: "56px",
          margin: "0 auto 16px",
          borderRadius: radius.pill,
          background: tokens.terracottaSoft,
          color: tokens.terracotta,
          display: "grid",
          placeItems: "center",
        }}
      >
        <Package size={28} strokeWidth={1.5} />
      </div>
      <div
        style={{
          fontSize: "22px",
          color: tokens.ink,
          lineHeight: 1.2,
        }}
      >
        No deliveries scheduled.
      </div>
      <div
        style={{
          fontSize: "14px",
          color: tokens.muted,
          marginTop: "6px",
          maxWidth: "32ch",
          margin: "6px auto 0",
        }}
      >
        Harvest events marked <em>delivery</em> show up here.
      </div>
    </div>
  );
}

const iconBtn = {
  background: "transparent",
  border: `1px solid ${tokens.line}`,
  color: tokens.muted,
  width: "44px",
  height: "44px",
  borderRadius: radius.sm,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "inherit",
  transition: "background 0.15s ease, color 0.15s ease",
} as const;
